import { Line3, Vector3 } from 'three'

const INTERSECTION_THRESHOLD = 25
const SNAP_THRESHOLD = 49
const ORTHO_SNAP_THRESHOLD = 25

export type SnapLine = {
  id: string
  line: Line3
}

export type SnapPoint = {
  id: string
  point: Vector3
}

export type SnapData = {
  objectID: string
  snapPoint: Vector3
  objectLine: Line3
  distance: number
  previousObjectID?: string | undefined
  previousObjectLine?: Line3 | undefined
  previousDistance?: number | undefined
}

export type SnapDataPoint = {
  objectID: string
  snapPoint: Vector3
  distance: number
}

export function snapToLineOrIntersection(
  lines: SnapLine[],
  point: Vector3,
  clamp: boolean = true
): SnapData | undefined {
  const relevantLines = lines
    .map(({ id, line }) => {
      const target = new Vector3()
      line.closestPointToPoint(point, clamp, target)
      const targetDistance = target.distanceToSquared(point)

      return { id, target, targetDistance, line }
    })
    .sort((a, b) => a.targetDistance - b.targetDistance)

  if (relevantLines.length >= 2) {
    const [first, second] = relevantLines

    const intersectedPoint = intersectLines(first.line, second.line) ?? undefined
    if (intersectedPoint && intersectedPoint.distanceToSquared(point) <= INTERSECTION_THRESHOLD) {
      return {
        objectID: first.id,
        snapPoint: intersectedPoint,
        objectLine: first.line,
        distance: first.targetDistance,
        previousObjectID: second.id,
        previousObjectLine: second.line,
        previousDistance: second.targetDistance,
      }
    }
    if (first.target.distanceToSquared(point) <= SNAP_THRESHOLD) {
      return { objectID: first.id, snapPoint: first.target, objectLine: first.line, distance: first.targetDistance }
    }
  } else if (relevantLines.length >= 1) {
    const [line] = relevantLines

    if (line.target.distanceToSquared(point) <= SNAP_THRESHOLD) {
      return { objectID: line.id, snapPoint: line.target, objectLine: line.line, distance: line.targetDistance }
    }
  }

  return undefined
}

/**
 * Finds which line from {@link lines} is closest the given {@link point}, and returns the closest point on it
 */
export function snapToLine(lines: SnapLine[], point: Vector3, clamp: boolean = true): SnapData | undefined {
  const target = new Vector3()
  let distance = Infinity
  let objectID: string | undefined = undefined
  let objectLine: Line3 | undefined = undefined
  const snapPoint = new Vector3()
  for (const { id, line } of lines) {
    line.closestPointToPoint(point, clamp, target)
    const targetDistance = target.distanceToSquared(point)
    if (targetDistance < distance) {
      distance = targetDistance
      objectID = id
      objectLine = line
      snapPoint.copy(target)
    }
  }
  if (objectID === undefined) {
    return undefined
  }
  return { objectID, snapPoint, objectLine: objectLine!, distance }
}

/**
 * Finds which point from {@link points} is closest to the given {@link point}, and returns it
 */
export function snapToPoint(points: SnapPoint[], point: Vector3): SnapDataPoint | undefined {
  let distance = Infinity
  let objectID: string | undefined = undefined
  const snapPoint = new Vector3()

  for (const snap of points) {
    const targetDistance = snap.point.distanceToSquared(point)
    if (targetDistance < distance) {
      distance = targetDistance
      objectID = snap.id
      snapPoint.copy(snap.point)
    }
  }
  if (objectID === undefined || distance >= SNAP_THRESHOLD) {
    return undefined
  }
  return { objectID, snapPoint, distance }
}

const ORTHO_OFFSETS = [
  { x: 1, y: 0, z: 0 },
  { x: 0, y: 1, z: 0 },
]

/**
 * Finds which point from {@link points} has an orthogonal line that is closest to the given {@link point}, and returns it
 */
export function snapToPointOrtho(points: SnapPoint[], point: Vector3): SnapData | undefined {
  const target = new Vector3()
  let distance = Infinity
  let objectID: string | undefined = undefined
  const objectLine = new Line3()
  const snapPoint = new Vector3()

  const line = new Line3()

  for (const snap of points) {
    line.start.copy(snap.point)

    for (const ortho of ORTHO_OFFSETS) {
      line.end.copy(snap.point).add(ortho)

      line.closestPointToPoint(point, false, target)
      const targetDistance = target.distanceToSquared(point)
      if (
        targetDistance < distance ||
        (targetDistance === distance && objectLine.distanceSq() > snap.point.distanceToSquared(target))
      ) {
        distance = targetDistance
        objectID = snap.id
        objectLine.copy(line).end.copy(target)
        snapPoint.copy(target)
      }
    }
  }

  if (objectID === undefined || distance >= ORTHO_SNAP_THRESHOLD) {
    return undefined
  }
  return { objectID, snapPoint, objectLine: objectLine, distance }
}

/**
 * Snaps {@link point} to the closest line or point from the given collections
 */
export function snapTo(
  lines: SnapLine[],
  points: SnapPoint[],
  point: Vector3,
  clamp: boolean = true
): SnapDataPoint | SnapData | undefined {
  const snapLineResult = snapToLine(lines, point, clamp)
  const snapPointResult = snapToPoint(points, point)

  if (!snapLineResult || !snapPointResult) {
    return undefined
  }
  if (snapLineResult && !snapPointResult) {
    return snapLineResult
  } else if (snapPointResult && !snapLineResult) {
    return snapPointResult
  }

  if (snapLineResult.distance <= snapPointResult.distance) {
    return snapLineResult
  } else {
    return snapPointResult
  }
}

/**
 * Snaps the point to be cardinal to the other point
 */
export function snapToCardinals(base: Vector3, exterior: Vector3): Vector3 {
  const dx = Math.abs(base.x - exterior.x)
  const dy = Math.abs(base.y - exterior.y)

  if (dx >= dy) {
    return exterior.clone().setY(base.y)
  } else {
    return exterior.clone().setX(base.x)
  }
}

/**
 * Computes the intersection of two lines in 2D
 */
export function intersectLines(lineA: Line3, lineB: Line3): Vector3 | null {
  const {
    start: { x: x1, y: y1 },
    end: { x: x2, y: y2 },
  } = lineA
  const {
    start: { x: x3, y: y3 },
    end: { x: x4, y: y4 },
  } = lineB

  const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1)

  if (denominator === 0) {
    return null
  }

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator

  const x = x1 + ua * (x2 - x1)
  const y = y1 + ua * (y2 - y1)

  return new Vector3(x, y, 0)
}
